export class ApiError extends Error {
  constructor({ code, status, message, details, requestId }) {
    super(message ?? code)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details ?? null
    this.requestId = requestId ?? null
  }

  get isNetwork() {
    return this.code === 'network'
  }
}

const WRITE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE'])

export function createHttp({ fetchImpl = globalThis.fetch, onUnauthorized, onCsrfToken } = {}) {
  let token = null

  async function ensureToken() {
    if (token) return token
    const response = await fetchImpl('/api/csrf', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    const body = await response.json()
    token = body.token
    return token
  }

  async function parse(response) {
    if (response.status === 204 || response.headers.get('Content-Length') === '0') return null
    const text = await response.text()
    if (!text) return null
    try {
      return JSON.parse(text)
    } catch {
      return null
    }
  }

  async function send(method, url, body, { retried = false, skipUnauthorizedHandler = false } = {}) {
    const headers = { Accept: 'application/json' }
    const init = { method, credentials: 'same-origin', headers }

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    }
    if (WRITE_METHODS.has(method)) headers['X-CSRF-Token'] = await ensureToken()

    let response
    try {
      response = await fetchImpl(url, init)
    } catch (cause) {
      throw new ApiError({ code: 'network', status: 0, message: cause.message })
    }

    const rotated = response.headers.get('X-CSRF-Token')
    if (rotated) {
      token = rotated
      onCsrfToken?.(rotated)
    }

    if (response.ok) return parse(response)

    const payload = await parse(response)
    const error = new ApiError({
      code: payload?.error?.code ?? 'unknown',
      status: response.status,
      message: payload?.error?.message,
      details: payload?.error?.details,
      requestId: payload?.error?.requestId,
    })

    if (error.status === 403 && error.code === 'csrf' && !retried) {
      token = null
      await ensureToken()
      return send(method, url, body, { retried: true, skipUnauthorizedHandler })
    }
    // `skipUnauthorizedHandler` exists for calls that verify a password as their whole
    // purpose (changing it, deleting the account): a 401 there means "you typed the wrong
    // current password," a normal, locally-recoverable outcome -- not "your session is no
    // longer valid." Left ungated, this unconditional call is what silently signed a user
    // out from under themselves on a mistyped current password while their real session
    // was still perfectly good (see auth.js's changePassword/deleteAccount for the two
    // call sites that opt out; every other endpoint keeps this behavior unchanged).
    if (error.status === 401 && !skipUnauthorizedHandler) onUnauthorized?.(error)
    throw error
  }

  return {
    get: (url, options) => send('GET', url, undefined, options),
    post: (url, body, options) => send('POST', url, body ?? {}, options),
    patch: (url, body, options) => send('PATCH', url, body ?? {}, options),
    del: (url, body, options) => send('DELETE', url, body, options),
    setCsrfToken(value) {
      token = value
    },
    csrfToken: () => token,
  }
}
